import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getOriginLink,
  getTaskEntityNames,
  resolveAllTaskLinks
} from "@/entities/tasks/taskLinks";
import { TaskData, TaskLink } from "@/entities/tasks/types";
import { useTaskLinkNames } from "@/components/layouts/TasksDrawer/useTaskLinkNames";
import { useCompanies } from "@/pages/companies/hooks";
import { useEntitySearch } from "@/services/hooks/entities/useEntitySearch";
import { EntityData } from "@/services/api/types";
import { EntityType } from "@/services/hooks/entities/useEntityType";

interface Params {
  enabled: boolean;
  isOpen: boolean;
  isEditMode: boolean;
  editTaskId: string | null;
  taskData?: TaskData;
  entityType?: EntityType;
  entityData?: EntityData;
}

export const useTaskLinks = ({
  enabled,
  isOpen,
  isEditMode,
  editTaskId,
  taskData,
  entityType,
  entityData
}: Params) => {
  const [links, setLinks] = useState<TaskLink[]>([]);
  const taskEntityIdsKey = taskData?.entityIds?.join(",");

  useEffect(() => {
    if (!enabled || !isOpen) return;
    if (isEditMode) {
      const isTaskLoaded =
        Boolean(taskData) && (!editTaskId || taskData?.id === editTaskId);
      setLinks(
        isTaskLoaded
          ? resolveAllTaskLinks(
              getTaskEntityNames(taskData?.entityNames, taskData?.entities),
              taskData?.entityIds
            )
          : []
      );
      return;
    }
    const originLink = getOriginLink(entityType, entityData);
    setLinks(originLink ? [originLink] : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on ids, not object identity, so links are not re-seeded on every parent render
  }, [
    enabled,
    isOpen,
    isEditMode,
    editTaskId,
    taskData?.id,
    taskEntityIdsKey,
    entityType,
    entityData?.id
  ]);

  const originLink = useMemo(
    () => (isEditMode ? null : getOriginLink(entityType, entityData)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on id, not object identity
    [isEditMode, entityType, entityData?.id]
  );

  const suggestionCompanyId = isEditMode
    ? undefined
    : entityType === EntityType.COMPANY
      ? (entityData?.id as string | undefined)
      : (entityData?.companyId as string | undefined);
  const companyFilters = useMemo(
    () => (suggestionCompanyId ? { companyId: suggestionCompanyId } : undefined),
    [suggestionCompanyId]
  );

  const { entities: suggestedCompanies } = useCompanies(
    enabled && suggestionCompanyId ? [suggestionCompanyId] : []
  );
  const { options: suggestedPeople } = useEntitySearch({
    entityType: EntityType.PERSON,
    filters: enabled ? companyFilters : undefined
  });
  const { options: suggestedContracts } = useEntitySearch({
    entityType: EntityType.CONTRACT,
    filters: enabled ? companyFilters : undefined
  });

  const suggestions = useMemo<TaskLink[]>(() => {
    const suggestedCompany = suggestedCompanies?.[0];
    const originPartnerId = entityData?.partner as string | undefined;
    const toLinks = (
      options: { id: string | number | null; displayName: string }[],
      linkEntityType: EntityType
    ) =>
      options.reduce<TaskLink[]>((options_, option) => {
        if (option.id == null) return options_;
        options_.push({
          entityId: String(option.id),
          entityName: option.displayName,
          entityType: linkEntityType
        });
        return options_;
      }, []);

    return [
      ...(suggestedCompany?.id
        ? [
            {
              entityId: suggestedCompany.id,
              entityName: suggestedCompany.name ?? "",
              entityType: EntityType.COMPANY
            }
          ]
        : []),
      ...(originPartnerId
        ? [
            {
              entityId: originPartnerId,
              entityName: (entityData?.partnerName as string) ?? "",
              entityType: EntityType.PARTNER
            }
          ]
        : []),
      ...toLinks(suggestedContracts, EntityType.CONTRACT),
      ...toLinks(suggestedPeople, EntityType.PERSON)
    ];
  }, [
    suggestedCompanies,
    suggestedPeople,
    suggestedContracts,
    entityData?.partner,
    entityData?.partnerName
  ]);

  const namedLinks = useTaskLinkNames(links);

  const addLink = useCallback(
    (link: TaskLink) =>
      setLinks((current) =>
        current.some((existing) => existing.entityId === link.entityId)
          ? current
          : [...current, link]
      ),
    []
  );

  const removeLink = useCallback(
    (entityId: string) =>
      setLinks((current) =>
        current.filter((existing) => existing.entityId !== entityId)
      ),
    []
  );

  return { links, namedLinks, suggestions, originLink, addLink, removeLink };
};
